// src/routes/AppRoutes.tsx

import { lazy } from 'react';
import { Route, Routes, Navigate } from 'react-router-dom';
import { ProtectedRoute } from './ProtectedRoute';
import { LegacyConsultationRedirect } from './LegacyConsultationRedirect';

// Core Pages
const Index = lazy(() => import('../pages/Index'));
const LoginPage = lazy(() => import('../pages/LoginPage'));
const SignupPage = lazy(() => import('../pages/SignupPage'));
const ForgotPasswordPage = lazy(() => import('../pages/ForgotPasswordPage'));

// Onboarding Pages
const RoleSelection = lazy(() => import('../pages/RoleSelection'));
const HospitalClinicOnboarding = lazy(() => import('../pages/HospitalClinicOnboarding'));
const InsuranceOnboarding = lazy(() => import('../pages/InsuranceOnboarding'));
const LaboratoryOnboarding = lazy(() => import('../pages/LaboratoryOnboarding'));

// Other Pages
const Profile = lazy(() => import('../pages/Profile'));
const Settings = lazy(() => import('../pages/Settings'));
const Patients = lazy(() => import('../pages/Patients'));
const NewPatient = lazy(() => import('../pages/NewPatient'));
const PatientDetails = lazy(() => import('../pages/PatientDetails'));
const Features = lazy(() => import('../pages/Features'));
const Pricing = lazy(() => import('../pages/Pricing'));
const Integrations = lazy(() => import('../pages/Integrations'));
const NhisIntegration = lazy(() => import('../pages/NhisIntegration'));
const HelpCenter = lazy(() => import('../pages/HelpCenter'));
const Contact = lazy(() => import('../pages/Contact'));
const Privacy = lazy(() => import('../pages/Privacy'));
const Terms = lazy(() => import('../pages/Terms'));
const EmergencyPage = lazy(() => import('../pages/EmergencyPage'));

// New Pages
const PrescriptionReceipt = lazy(() => import('../pages/PrescriptionReceipt'));
const TestOrderReceipt = lazy(() => import('../pages/TestOrderReceipt'));
const ConsultationList = lazy(() => import('../pages/ConsultationList'));
const ConsultationDetail = lazy(() => import('../pages/ConsultationDetail'));
const NewConsultation = lazy(() => import('../pages/NewConsultation'));
const NotificationsPage = lazy(() => import('../pages/NotificationsPage'));

// Dashboard
const Dashboard = lazy(() => import('../components/Dashboard/Dashboard'));
const Insurance = lazy(() => import('../components/Dashboard/insurance-dashboard/Insurance'));

// Appointments
const Appointments = lazy(() => import('../pages/Appointments'));

// Insurance Pages
const InsurancePlans = lazy(() => import('../pages/insurance/InsurancePlans'));
const PlanDetails = lazy(() => import('../pages/insurance/PlanDetails'));
const CreatePlan = lazy(() => import('../pages/insurance/CreatePlan'));
const EditPlan = lazy(() => import('../pages/insurance/EditPlan'));
const ClaimsList = lazy(() => import('../pages/insurance/ClaimsList'));
const ClaimDetails = lazy(() => import('../pages/insurance/ClaimDetails'));
const SubmitClaim = lazy(() => import('../pages/insurance/SubmitClaim'));
const ClaimApproval = lazy(() => import('../pages/insurance/ClaimApproval'));
const EnrollmentList = lazy(() => import('../pages/insurance/EnrollmentList'));
const NewEnrollment = lazy(() => import('../pages/insurance/NewEnrollment'));
const ProviderNetwork = lazy(() => import('../pages/insurance/ProviderNetwork'));
const NetworkDetails = lazy(() => import('../pages/insurance/NetworkDetails'));
const AddProvider = lazy(() => import('../pages/insurance/AddProvider'));
const PreAuthorization = lazy(() => import('../pages/insurance/PreAuthorization'));
const VerifyEligibility = lazy(() => import('../pages/insurance/VerifyEligibility'));
const InsuranceReports = lazy(() => import('../pages/insurance/Reports'));
const InsuranceProfile = lazy(() => import('../pages/insurance/InsuranceProfile'));

// Laboratory Pages
const LabDashboard = lazy(() => import('../components/Dashboard/laboratory-dashboard/LaboratoryDashboard'));
const TestRequests = lazy(() => import('../pages/lab/TestRequests'));
const OngoingTests = lazy(() => import('../pages/lab/OngoingTests'));
const CompletedTests = lazy(() => import('../pages/lab/CompletedTests'));
const UploadResults = lazy(() => import('../pages/lab/UploadResults'));
const PatientsList = lazy(() => import('../pages/lab/PatientsList'));
const LabReports = lazy(() => import('../pages/lab/Reports'));
const LabSettings = lazy(() => import('../pages/lab/Settings'));
const LabProfile = lazy(() => import('../pages/lab/LabProfile'));

// 404 Page
const NotFound = lazy(() => import('../pages/NotFound'));

export const AppRoutes: React.FC = () => {
  return (
    <Routes>
      {/* Public Routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute allowUnauthenticated>
            <Index />
          </ProtectedRoute>
        }
      />
      <Route
        path="/login"
        element={
          <ProtectedRoute allowUnauthenticated>
            <LoginPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/signup"
        element={
          <ProtectedRoute allowUnauthenticated>
            <SignupPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <ProtectedRoute allowUnauthenticated>
            <ForgotPasswordPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/features"
        element={
          <ProtectedRoute allowUnauthenticated>
            <Features />
          </ProtectedRoute>
        }
      />
      <Route
        path="/pricing"
        element={
          <ProtectedRoute allowUnauthenticated>
            <Pricing />
          </ProtectedRoute>
        }
      />
      <Route
        path="/integrations"
        element={
          <ProtectedRoute allowUnauthenticated>
            <Integrations />
          </ProtectedRoute>
        }
      />
      <Route
        path="/nhis-integration"
        element={
          <ProtectedRoute allowUnauthenticated>
            <NhisIntegration />
          </ProtectedRoute>
        }
      />
      <Route
        path="/help"
        element={
          <ProtectedRoute allowUnauthenticated>
            <HelpCenter />
          </ProtectedRoute>
        }
      />
      <Route
        path="/contact"
        element={
          <ProtectedRoute allowUnauthenticated>
            <Contact />
          </ProtectedRoute>
        }
      />
      <Route
        path="/privacy"
        element={
          <ProtectedRoute allowUnauthenticated>
            <Privacy />
          </ProtectedRoute>
        }
      />
      <Route
        path="/terms"
        element={
          <ProtectedRoute allowUnauthenticated>
            <Terms />
          </ProtectedRoute>
        }
      />

      {/* Onboarding Routes */}
      <Route
        path="/onboarding/role-selection"
        element={
          <ProtectedRoute>
            <RoleSelection />
          </ProtectedRoute>
        }
      />
      <Route
        path="/onboarding/hospital-clinic"
        element={
          <ProtectedRoute>
            <HospitalClinicOnboarding />
          </ProtectedRoute>
        }
      />
      <Route
        path="/onboarding/insurance"
        element={
          <ProtectedRoute>
            <InsuranceOnboarding />
          </ProtectedRoute>
        }
      />
      <Route
        path="/onboarding/laboratory"
        element={
          <ProtectedRoute>
            <LaboratoryOnboarding />
          </ProtectedRoute>
        }
      />

      {/* Dashboard Routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />

      {/* Profile and Settings */}
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <Profile />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        }
      />

      {/* Patient Management Routes */}
      <Route
        path="/patients"
        element={
          <ProtectedRoute>
            <Patients />
          </ProtectedRoute>
        }
      />
      <Route
        path="/patients/new"
        element={
          <ProtectedRoute>
            <NewPatient />
          </ProtectedRoute>
        }
      />
      <Route
        path="/patients/:patientId"
        element={
          <ProtectedRoute>
            <PatientDetails />
          </ProtectedRoute>
        }
      />
      <Route
        path="/patient/:patientId"
        element={
          <ProtectedRoute>
            <PatientDetails />
          </ProtectedRoute>
        }
      />

      {/* Appointments */}
      <Route
        path="/appointments"
        element={
          <ProtectedRoute>
            <Appointments />
          </ProtectedRoute>
        }
      />

      {/* Consultation Routes */}
      <Route
        path="/consultation-list"
        element={
          <ProtectedRoute>
            <ConsultationList />
          </ProtectedRoute>
        }
      />
      <Route
        path="/consultation-detail/:consultationId"
        element={
          <ProtectedRoute>
            <ConsultationDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/new-consultation"
        element={
          <ProtectedRoute>
            <NewConsultation />
          </ProtectedRoute>
        }
      />

      {/* Prescription Receipt */}
      <Route
        path="/prescription-receipt/:prescriptionId"
        element={
          <ProtectedRoute>
            <PrescriptionReceipt />
          </ProtectedRoute>
        }
      />

      {/* Test Order Receipt */}
      <Route
        path="/test-order-receipt/:testOrderId"
        element={
          <ProtectedRoute>
            <TestOrderReceipt />
          </ProtectedRoute>
        }
      />

      {/* Notifications */}
      <Route
        path="/notifications"
        element={
          <ProtectedRoute>
            <NotificationsPage />
          </ProtectedRoute>
        }
      />

      {/* Emergency Page */}
      <Route
        path="/emergency"
        element={
          <ProtectedRoute>
            <EmergencyPage />
          </ProtectedRoute>
        }
      />

      {/* INSURANCE ROUTES */}
      {/* ====================================== */}
      {/* Insurance Dashboard */}
      <Route
        path="/insurance/dashboard"
        element={
          <ProtectedRoute allowedRoles={['insurance']}>
            <Insurance />
          </ProtectedRoute>
        }
      />

      {/* Insurance Plans Routes */}
      <Route
        path="/insurance/plans"
        element={
          <ProtectedRoute allowedRoles={['insurance']}>
            <InsurancePlans />
          </ProtectedRoute>
        }
      />
      <Route
        path="/insurance/plan/:planId"
        element={
          <ProtectedRoute allowedRoles={['insurance']}>
            <PlanDetails />
          </ProtectedRoute>
        }
      />
      <Route
        path="/insurance/create-plan"
        element={
          <ProtectedRoute allowedRoles={['insurance']}>
            <CreatePlan />
          </ProtectedRoute>
        }
      />
      <Route
        path="/insurance/edit-plan/:planId"
        element={
          <ProtectedRoute allowedRoles={['insurance']}>
            <EditPlan />
          </ProtectedRoute>
        }
      />

      {/* Claims Management Routes */}
      <Route
        path="/insurance/claims"
        element={
          <ProtectedRoute allowedRoles={['insurance']}>
            <ClaimsList />
          </ProtectedRoute>
        }
      />
      <Route
        path="/insurance/claim-details/:claimId"
        element={
          <ProtectedRoute allowedRoles={['insurance']}>
            <ClaimDetails />
          </ProtectedRoute>
        }
      />
      <Route
        path="/insurance/submit-claim"
        element={
          <ProtectedRoute allowedRoles={['insurance']}>
            <SubmitClaim />
          </ProtectedRoute>
        }
      />
      <Route
        path="/insurance/claim-approval/:claimId"
        element={
          <ProtectedRoute allowedRoles={['insurance']}>
            <ClaimApproval />
          </ProtectedRoute>
        }
      />

      {/* Enrollment Management Routes */}
      <Route
        path="/insurance/enrollments"
        element={
          <ProtectedRoute allowedRoles={['insurance']}>
            <EnrollmentList />
          </ProtectedRoute>
        }
      />
      <Route
        path="/insurance/new-enrollment"
        element={
          <ProtectedRoute allowedRoles={['insurance']}>
            <NewEnrollment />
          </ProtectedRoute>
        }
      />

      {/* Provider Network Routes */}
      <Route
        path="/insurance/provider-network"
        element={
          <ProtectedRoute allowedRoles={['insurance']}>
            <ProviderNetwork />
          </ProtectedRoute>
        }
      />
      <Route
        path="/insurance/network/:networkId"
        element={
          <ProtectedRoute allowedRoles={['insurance']}>
            <NetworkDetails />
          </ProtectedRoute>
        }
      />
      <Route
        path="/insurance/add-provider"
        element={
          <ProtectedRoute allowedRoles={['insurance']}>
            <AddProvider />
          </ProtectedRoute>
        }
      />

      {/* Authorization & Verification Routes */}
      <Route
        path="/insurance/pre-authorization"
        element={
          <ProtectedRoute allowedRoles={['insurance']}>
            <PreAuthorization />
          </ProtectedRoute>
        }
      />
      <Route
        path="/insurance/verify-eligibility"
        element={
          <ProtectedRoute allowedRoles={['insurance']}>
            <VerifyEligibility />
          </ProtectedRoute>
        }
      />

      {/* Reports Route */}
      <Route
        path="/insurance/reports"
        element={
          <ProtectedRoute allowedRoles={['insurance']}>
            <InsuranceReports />
          </ProtectedRoute>
        }
      />

      {/* Insurance Profile Route — Settings now lives here.
          Old /insurance/settings bookmarks and links redirect. */}
      <Route
        path="/insurance/settings"
        element={<Navigate to="/insurance/profile" replace />}
      />
      <Route
        path="/insurance/profile"
        element={
          <ProtectedRoute allowedRoles={['insurance']}>
            <InsuranceProfile />
          </ProtectedRoute>
        }
      />

      {/* LABORATORY ROUTES */}
      {/* ====================================== */}
      {/* Laboratory Dashboard */}
      <Route
        path="/lab/dashboard"
        element={
          <ProtectedRoute allowedRoles={['laboratory']}>
            <LabDashboard />
          </ProtectedRoute>
        }
      />
      {/* Test Management Routes */}
      <Route
        path="/lab/test-requests"
        element={
          <ProtectedRoute allowedRoles={['laboratory']}>
            <TestRequests />
          </ProtectedRoute>
        }
      />
      <Route
        path="/lab/ongoing-tests"
        element={
          <ProtectedRoute allowedRoles={['laboratory']}>
            <OngoingTests />
          </ProtectedRoute>
        }
      />
      <Route
        path="/lab/completed-tests"
        element={
          <ProtectedRoute allowedRoles={['laboratory']}>
            <CompletedTests />
          </ProtectedRoute>
        }
      />
      {/* Results Upload Route */}
      <Route
        path="/lab/upload-results"
        element={
          <ProtectedRoute allowedRoles={['laboratory']}>
            <UploadResults />
          </ProtectedRoute>
        }
      />
      {/* Patients Management Route */}
      <Route
        path="/lab/patients"
        element={
          <ProtectedRoute allowedRoles={['laboratory']}>
            <PatientsList />
          </ProtectedRoute>
        }
      />
      {/* Profile Route */}
      <Route
        path="/lab/profile"
        element={
          <ProtectedRoute allowedRoles={['laboratory']}>
            <LabProfile />
          </ProtectedRoute>
        }
      />
      {/* Notifications Route */}
      <Route
        path="/lab/notifications"
        element={
          <ProtectedRoute allowedRoles={['laboratory']}>
            <NotificationsPage />
          </ProtectedRoute>
        }
      />
      {/* Reports & Settings Routes */}
      <Route
        path="/lab/reports"
        element={
          <ProtectedRoute allowedRoles={['laboratory']}>
            <LabReports />
          </ProtectedRoute>
        }
      />
      <Route
        path="/lab/settings"
        element={
          <ProtectedRoute allowedRoles={['laboratory']}>
            <LabSettings />
          </ProtectedRoute>
        }
      />

      {/* Legacy redirect routes */}
      <Route path="/consultations" element={<Navigate to="/consultation-list" replace />} />
      <Route path="/consultations/new" element={<Navigate to="/new-consultation" replace />} />
      <Route path="/consultations/:consultationId" element={<LegacyConsultationRedirect />} />

      {/* Unauthorized Route */}
      <Route
        path="/unauthorized"
        element={
          <div className="min-h-screen flex items-center justify-center text-center text-gray-600">
            Unauthorized Access
          </div>
        }
      />

      {/* 404 Page */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

export default AppRoutes;